import { motion } from "framer-motion";
import {
  LineChart,
  BarChart3,
  Activity,
  Target,
  CandlestickChart,
  Flame,
  Shield,
  Coins,
  Bitcoin,
} from "lucide-react";
import MarketAmbient from "@/components/MarketAmbient";

const topics = [
  {
    icon: LineChart,
    title: "Stock Market Analysis",
    desc: "Deep-dive into markets with data-driven, unbiased analysis.",
    color: "hsl(var(--brand-orange))",
  },
  {
    icon: BarChart3,
    title: "Nifty 50 & Bank Nifty",
    desc: "Daily updates, key levels & directional bias for Indian indices.",
    color: "hsl(var(--brand-green))",
  },
  {
    icon: Activity,
    title: "Swing & Intraday Trading",
    desc: "Setups that work — timeframes tuned to your lifestyle.",
    color: "hsl(var(--brand-orange))",
  },
  {
    icon: Target,
    title: "Options Strategies",
    desc: "Directional and non-directional strategies with risk-defined structure.",
    color: "hsl(217 91% 60%)",
  },
  {
    icon: CandlestickChart,
    title: "Technical Analysis",
    desc: "Price action, trends, and structure — the language of the tape.",
    color: "hsl(var(--brand-green))",
  },
  {
    icon: Flame,
    title: "Candlestick Patterns",
    desc: "Read intent behind every wick, body and close.",
    color: "hsl(var(--brand-orange))",
  },
  {
    icon: Shield,
    title: "Risk & Psychology",
    desc: "Position sizing, drawdown control & the trader's mindset.",
    color: "hsl(217 91% 60%)",
  },
  {
    icon: Coins,
    title: "Long-Term Investing",
    desc: "Compound with conviction — build durable wealth for decades.",
    color: "hsl(var(--brand-green))",
  },
  {
    icon: Bitcoin,
    title: "Crypto Insights",
    desc: "Bitcoin, Ethereum & altcoins — signal over noise.",
    color: "hsl(var(--brand-orange))",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 22 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

const WhatWeCover = () => {
  return (
    <section id="topics" className="section relative">
      <div className="pointer-events-none absolute inset-0 grid-bg" aria-hidden="true" />
      <MarketAmbient opacity={0.06} />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip" data-testid="topics-badge">What we cover</span>
          <h2
            className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="topics-heading"
          >
            Everything you need to{" "}
            <span className="text-gradient-brand">navigate the markets</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
            Nine focused pillars — from candlestick basics to crypto insights — built to grow your edge, one video at a time.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {topics.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.article
                key={t.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card/40 p-6 lift"
                data-testid={`topic-card-${i}`}
              >
                <div
                  className="absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-40"
                  style={{ background: t.color }}
                  aria-hidden="true"
                />
                <div
                  className="relative inline-flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: `${t.color.replace(")", " / 0.12)")}`,
                    color: t.color,
                  }}
                >
                  <Icon size={22} strokeWidth={2.2} />
                </div>
                <h3 className="relative mt-5 font-display text-lg font-bold tracking-tight">
                  {t.title}
                </h3>
                <p className="relative mt-2 text-sm leading-relaxed text-muted-foreground">
                  {t.desc}
                </p>
                <div className="relative mt-5 h-px w-full bg-gradient-to-r from-border via-border to-transparent" />
                <p className="relative mt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Pillar 0{i + 1}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatWeCover;
